<script setup lang="ts">
/**
 * The composer.
 *
 * Enter sends, Shift+Enter makes a new line — the convention every chat product
 * shares, and the hint under the field says so rather than leaving the visitor to
 * discover it by sending half a sentence.
 *
 * The text is cleared OPTIMISTICALLY, the moment it is handed off. That is safe
 * only because a failed send keeps the message on screen with the visitor's words
 * in it and a retry button (see `Transcript`) — the text is never actually lost,
 * it just moves from the field into the transcript. A composer that cleared on
 * success instead would leave the visitor staring at a field they cannot tell is
 * pending, on a connection where the round trip is the slow part.
 */
import { ref, useTemplateRef, watch } from 'vue';

import { useText } from '../vendor/i18n';
import { OwButton, OwTextarea } from '../vendor/ui';

const props = defineProps<{
  /** The host page just opened the panel; the visitor came here to type. */
  readonly autoFocus: boolean;
}>();

const emit = defineEmits<{ send: [body: string] }>();

const text = useText();
const draft = ref('');
const field = useTemplateRef<InstanceType<typeof OwTextarea>>('field');

watch(
  () => props.autoFocus,
  (focus) => {
    if (focus) {
      field.value?.focus();
    }
  },
  { immediate: true, flush: 'post' },
);

function submit(): void {
  const body = draft.value.trim();
  if (body === '') {
    return;
  }
  draft.value = '';
  emit('send', body);
}

function onKeyDown(event: KeyboardEvent): void {
  // `event.shiftKey` and not `event.altKey || event.shiftKey`: on a phone
  // keyboard Enter is the only key there is, and the software keyboard sends it
  // without modifiers — which is why the send button is always present and never
  // hidden behind "press Enter".
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    submit();
  }
}
</script>

<template>
  <form class="ow-composer" @submit.prevent="submit">
    <OwTextarea
      ref="field"
      v-model="draft"
      class="ow-composer__field"
      :rows="1"
      :label="text('widget.composer.placeholder')"
      described-by="ow-composer-hint"
      :placeholder="text('widget.composer.placeholder')"
      @keydown="onKeyDown"
    />
    <OwButton
      type="submit"
      variant="primary"
      class="ow-composer__send"
      :disabled="draft.trim() === ''"
    >
      {{ text('widget.composer.send') }}
    </OwButton>
    <p id="ow-composer-hint" class="ow-composer__hint">
      {{ text('widget.composer.hint') }}
    </p>
  </form>
</template>
